import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { message } from 'antd';
import { useAuth } from '../auth/AuthContext';
import useAppStore from './AppStore';

const DictationContext = createContext();

export const useDictation = () => useContext(DictationContext);

// Define your API endpoint. Adjust the URL if needed.
// const CORRECT_TEXT_ENDPOINT = 'http://localhost:8000/correct-text';
// const CORRECT_TEXT_ENDPOINT = `${process.env.REACT_APP_API_URL}/correct-text`;
const CORRECT_TEXT_ENDPOINT = 'https://greek.sporo.health/correct-text';

export const DictationProvider = ({ children }) => {
  const auth = useAuth();
  const { dictationEnabled, setDictationEnabled } = useAppStore();
  const [isDictating, setIsDictating] = useState(false);
  const [dictationStatus, setDictationStatus] = useState(''); // user visible message
  const [interimTranscript, setInterimTranscript] = useState(''); // used from downstream users, same content as latestPartialTranscriptRef
  const [wordRectifications, setWordRectifications] = useState([]); // session rectifications
  const recognitionRef = useRef(null);
  const autoRestartRef = useRef(false);
  const sessionTranscriptRef = useRef(''); // final text
  const latestPartialTranscriptRef = useRef(''); // appending interim results, used locally here
  const [processedText, setProcessedText] = useState(''); // Store processed dictation text here
  
  // Track callbacks by component ID
  const callbacksRef = useRef(new Map());
  // Track which component is active
  const [activeComponentId, setActiveComponentId] = useState(null);

  // --------------------------------------------------------------------------
  // Fetch dictionaries (word rectifications) and initialize wordRectifications list
  const fetchWordRectifications = async () => {
    try {
      if (!auth.user || !auth.user.access_token) {
        message.error('Authentication token not found. Please log in again.');
        return;
      }
      const token = auth.user.access_token;
      const response = await fetch(`${process.env.REACT_APP_API_URL}/dictionary/me`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'accept': 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`Error fetching dictionaries: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.dictionary) {
        return data.dictionary;
      } else {
        return [];
      }
    } catch (error) {
      console.error('Error fetching dictionaries:', error);
      // message.error('Failed to load dictionaries. Please try again.');
      return [];
    }
  };

  // Optionally, expose the refresh function in the context so that consumers can re-fetch if needed.

  // --------------------------------------------------------------------------
  // Call your correct‑text API to process the transcript.
  const processTranscript = async (transcript, wordRectifications = []) => {
    if (!transcript || transcript.trim() === '') return transcript;
    
    const predefinedWordRectifications = await fetchWordRectifications();
    try {
      const response = await fetch(CORRECT_TEXT_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${auth.user['access_token']}`,
        },
        body: JSON.stringify({
          text: transcript.trim(),
          dictionary: [...wordRectifications, ...predefinedWordRectifications] // Pass rectifications if provided
        })
      });
      if (response.ok) {
        const result = await response.json();
        let corrected = result.corrected_text;
        // Remove surrounding quotes if present.
        if (corrected.startsWith('"') && corrected.endsWith('"')) {
          corrected = corrected.slice(1, -1);
        }
        // If the API returns a message asking for more context, return the original transcript.
        if (
          corrected.toLowerCase().includes('please provide more context') ||
          corrected.toLowerCase().includes('please provide more information')
        ) {
          return transcript;
        } else {
          return corrected;
        }
      } else {
        console.error('Error processing transcript via correct‑text API');
        return transcript;
      }
    } catch (error) {
      console.error('Error processing transcript via correct‑text API:', error);
      return transcript;
    }
  };

  const startDictation = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      message.error('Speech recognition is not supported in your browser.');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onstart = () => {
      setDictationStatus('Listening...');
    };

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          final += transcript + ' '; // transcript contains the full text but the user may continue to speak
        } else {
          interim += transcript;
        }
      }
      sessionTranscriptRef.current += final;
      latestPartialTranscriptRef.current = interim;
      setInterimTranscript(interim);
    };

    recognition.onerror = (event) => {
      if (event.error === 'aborted') {
        console.warn('Speech recognition aborted.');
        return;
      }
      console.error('Speech recognition error', event.error);
      setDictationStatus('Error: ' + event.error);
      stopDictation(); // keep the text
    };

    recognition.onend = () => {
      if (autoRestartRef.current) {
        setTimeout(() => {
          startDictation();
        }, 300);
      } else {
        setIsDictating(false);
      }
    };

    autoRestartRef.current = true;
    sessionTranscriptRef.current = '';
    latestPartialTranscriptRef.current = '';
    recognition.start();
    setIsDictating(true);
    setDictationStatus('Listening...');
  };

  /*
   * Defaults to keeping the text when stopping dictation.
   */
  const stopDictation = async(keepText = true) => {
    setIsDictating(false);

    if (recognitionRef.current) {
      setDictationStatus(keepText ? 'Processing...': 'Aborting due to key press...');

      // Wait a short time to ensure we get all words or the user sees the aborting status
      await new Promise(resolve => {setTimeout(resolve, 1000);});

      recognitionRef.current.stop();
      autoRestartRef.current = false;
    }

    if (!keepText) {
      setInterimTranscript('');
      latestPartialTranscriptRef.current = '';
      sessionTranscriptRef.current = '';
      setDictationStatus('');
      return;
    }
    
    const transcriptToProcess =
      sessionTranscriptRef.current.trim() || latestPartialTranscriptRef.current.trim();
    
    if (transcriptToProcess) {
      const improvedText = await processTranscript(transcriptToProcess, wordRectifications);
      
      // Store the processed text in state
      setProcessedText(improvedText);
      
      // Call the active component's callback function if it exists
      if (activeComponentId && callbacksRef.current.has(activeComponentId)) {
        try {
          const activeCallback = callbacksRef.current.get(activeComponentId);
          if (activeCallback) {
            activeCallback(improvedText);
          }
        } catch (error) {
          console.error('Error in text insert callback:', error);
        }
      }
    }
    
    setInterimTranscript('');
    sessionTranscriptRef.current = '';
    latestPartialTranscriptRef.current = '';
    setDictationStatus('');
  };

  useEffect(() => {
    try {
      const userDetails = localStorage.getItem('userDetails');
      if (userDetails) {
        const parsedUserDetails = JSON.parse(userDetails);
        if (parsedUserDetails && typeof parsedUserDetails.dictation_enabled === 'boolean') {
          setDictationEnabled(parsedUserDetails.dictation_enabled);
        }
      }
    } catch (error) {
      console.error('Error reading dictation_enabled from localStorage:', error);
    }
  }, []); // Run only once on mount - should be done on login

  useEffect(() => {
    if (!dictationEnabled) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Shift') {
        e.preventDefault();
        if (!isDictating) {
          startDictation();
        }
      }
      else if (isDictating) {
        stopDictation(false); // discard text when another key was pressed
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'Shift' && isDictating) {
        stopDictation();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
    };
  }, [isDictating, dictationEnabled]);

  return (
    <DictationContext.Provider
      value={{
        isDictating,
        dictationStatus,
        interimTranscript,
        processedText, // Expose the processed text to consumers
        startDictation,
        stopDictation,
        wordRectifications,
        setWordRectifications,
        // Register a component with its callback and make it active
        registerComponent: (componentId, callback) => {
          callbacksRef.current.set(componentId, callback);
        },
        // Set which component is active (focused)
        setActiveComponent: (componentId) => {
          setActiveComponentId(componentId);
        },
        // For backward compatibility
        setActiveInput: (callback) => {
          const defaultId = 'default';
          callbacksRef.current.set(defaultId, callback);
          setActiveComponentId(defaultId);
        },
      }}
    >
      {children}
    </DictationContext.Provider>
  );
};

export default DictationProvider;
